import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Eraser } from 'lucide-react';
import { Button } from './button';

/**
 * Electronic Signature Pad Component
 * 
 * Allows users to draw or type their signature with legal acknowledgment.
 * Captures signature as base64 image for storage.
 */

export function SignaturePad({ 
  onSign, 
  onCancel, 
  signerName = '',
  acknowledgmentText = "I certify that the information provided is accurate and complete to the best of my knowledge.",
  submitLabel = "Sign & Submit"
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureType, setSignatureType] = useState('drawn'); // 'drawn' or 'typed'
  const [typedName, setTypedName] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#0D1B33';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const pos = getPos(e);
    setLastPos(pos);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setLastPos(pos);
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = () => {
    if (!acknowledged) return;
    
    let signatureData = null;
    
    if (signatureType === 'drawn') {
      if (!hasSignature) return;
      const canvas = canvasRef.current;
      signatureData = canvas.toDataURL('image/png');
    } else {
      if (!typedName.trim()) return;
    }
    
    onSign({
      signature_data: signatureData,
      signature_type: signatureType,
      typed_name: signatureType === 'typed' ? typedName : null,
      acknowledgment_accepted: acknowledged,
    });
  };

  const isValid = acknowledged && (
    (signatureType === 'drawn' && hasSignature) ||
    (signatureType === 'typed' && typedName.trim())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-[#0D1B33]">Electronic Signature</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-4">
          {/* Signer info */}
          {signerName && (
            <p className="text-sm text-gray-600 mb-4">
              Signing as: <span className="font-medium text-[#0D1B33]">{signerName}</span>
            </p>
          )}
          
          {/* Signature type toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSignatureType('drawn')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                signatureType === 'drawn' 
                  ? 'bg-[#176DFF] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Draw Signature
            </button>
            <button
              onClick={() => setSignatureType('typed')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                signatureType === 'typed' 
                  ? 'bg-[#176DFF] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Type Name
            </button>
          </div>
          
          {/* Drawing canvas */}
          {signatureType === 'drawn' && (
            <div className="mb-4">
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white relative">
                <canvas
                  ref={canvasRef}
                  width={350}
                  height={150}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-gray-300 text-sm">Sign here</span>
                  </div>
                )}
              </div>
              <button
                onClick={clearSignature}
                className="mt-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <Eraser className="w-4 h-4" />
                Clear
              </button>
            </div>
          )}
          
          {/* Typed signature */}
          {signatureType === 'typed' && (
            <div className="mb-4">
              <input
                type="text"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder="Type your full name"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-lg focus:outline-none focus:border-[#176DFF]"
                style={{ fontFamily: "'Dancing Script', cursive" }}
              />
              {typedName && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-2xl text-[#0D1B33]" style={{ fontFamily: "'Dancing Script', cursive" }}>
                    {typedName}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Legal acknowledgment */}
          <div className="bg-amber-50 rounded-lg p-4 mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-[#176DFF] focus:ring-[#176DFF]"
              />
              <span className="text-sm text-amber-900">
                {acknowledgmentText}
              </span>
            </label>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              className={`flex-1 ${isValid ? 'bg-[#176DFF] hover:bg-[#176DFF]/90' : 'bg-gray-300 cursor-not-allowed'}`}
              onClick={handleSubmit}
              disabled={!isValid}
            >
              <Check className="w-4 h-4 mr-2" />
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
